<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, reactive, watch } from 'vue'

import AddPlusIcon from '@/assets/icons/add-plus.svg?component'
import DeleteIcon from '@/assets/icons/delete.svg?component'
import { combineTrafficSourceCosts, separateTrafficSourceCosts } from '@/lib/adapters/traffic-source-costs.adapter'
import { getDefaultTrafficSourceGroup } from '@/lib/helpers/campaign.helpers'
import type { CampaignFormType } from '@/lib/types/campaign.types'
import type { GroupedTrafficSourceCostType, TrafficSourceCostsType } from '@/lib/types/traffic-source-costs.types'
import { useTrafficSourcesStore } from '@/stores/traffic-sources'
import {
  BoxComponent,
  ButtonComponent,
  FlexComponent,
  InputComponent,
  LoadingComponent,
  SelectComponent,
  TitleComponent,
  TypographyComponent,
} from '@/ui-library'

const props = withDefaults(
  defineProps<{
    currency: string
    groupBySource?: boolean
    invoiceAdvertiserDirectly?: CampaignFormType['invoiceAdvertiserDirectly']
    isChangingSourcesDisabled?: boolean
  }>(),
  {
    groupBySource: true,
  },
)
const trafficSourceCosts = defineModel<TrafficSourceCostsType[]>('trafficSourceCosts', { default: [] })
const groupedTrafficSourceCosts = reactive(
  trafficSourceCosts.value?.length
    ? combineTrafficSourceCosts(trafficSourceCosts.value, props.groupBySource)
    : [{ ...getDefaultTrafficSourceGroup(props.currency) }],
)
const trafficSourcesStore = useTrafficSourcesStore()
const { data: trafficSources, isLoading: isTrafficSourcesLoading } = storeToRefs(trafficSourcesStore)
const selectOptions = computed(() => trafficSources.value.map((item) => ({ label: item.name, value: item.source })))

const addItem = () => {
  groupedTrafficSourceCosts.push({ ...getDefaultTrafficSourceGroup(props.currency) })
}
const remove = (item: GroupedTrafficSourceCostType) => {
  const index = groupedTrafficSourceCosts.indexOf(item)
  if (index !== -1) {
    groupedTrafficSourceCosts.splice(index, 1)
  }
}
const removeSourcesInOtherGroups = (currenIndex: number) => {
  if (groupedTrafficSourceCosts.length > 1) {
    const sourceIndexes = groupedTrafficSourceCosts[currenIndex]?.sourceIndexes
    groupedTrafficSourceCosts.forEach((item, index) => {
      if (index !== currenIndex) {
        item.sourceIndexes = item.sourceIndexes.filter((source) => !sourceIndexes?.includes(source))
      }
    })
  }
}
const handleSelect = (index: number) => {
  removeSourcesInOtherGroups(index)
}
const handleAllSelect = (item: GroupedTrafficSourceCostType, index: number) => {
  item.sourceIndexes = item.sourceIndexes.length === trafficSources.value.length ? [] : trafficSources.value.map((item) => item.source)
  removeSourcesInOtherGroups(index)
}
watch(
  () => groupedTrafficSourceCosts,
  (newValue) => (trafficSourceCosts.value = separateTrafficSourceCosts(newValue)),
  { deep: true },
)
const cpcRules = [
  {
    validator: (_: unknown, value: string | number, callback: (e?: Error) => void) => {
      if (!value) {
        return callback()
      }

      const formattedValue = Number(value)
      if (isNaN(formattedValue)) {
        return callback(new Error('Field must be a valid number'))
      }
      if (getDecimalPlacesCount(value) > 2) {
        return callback(new Error('Field must have at most 2 decimal places'))
      }
      if (formattedValue < 0) {
        return callback(new Error('Field must be a positive number'))
      }
      if (formattedValue >= 1000) {
        return callback(new Error('Field must be less than 1000'))
      }

      return callback()
    },
    trigger: 'blur',
  },
]
const cpaRules = [
  {
    validator: (_: unknown, value: string, callback: (e?: Error) => void) => {
      if (!value) {
        return callback()
      }

      const formattedValue = Number(value)
      if (isNaN(formattedValue)) {
        return callback(new Error('Field must be a valid number'))
      }
      if (formattedValue < 0) {
        return callback(new Error('Field must be a positive number'))
      }
      if (formattedValue >= 100) {
        return callback(new Error('Field must be less than 100'))
      }

      return callback()
    },
    trigger: 'blur',
  },
]
function getDecimalPlacesCount(value: string | number): number {
  const valueString = typeof value === 'number' ? value.toString() : value
  return valueString.includes('.') ? valueString.split('.')[1]!.length : 0
}
</script>

<template>
  <BoxComponent v-for="(item, index) in groupedTrafficSourceCosts" :key="index" :mb="24" :p="16" class="container">
    <FlexComponent :gap="8" align="center">
      <div>
        <BoxComponent v-if="trafficSources.length && !isTrafficSourcesLoading" :mb="16">
          <SelectComponent
            v-model="item.sourceIndexes"
            data-test="trafficSourceCostsSelect"
            :options="selectOptions"
            placeholder="Select source"
            filterable
            multiple
            :disabled="isChangingSourcesDisabled"
            :on-item-select="() => handleSelect(index)"
          >
            <template #header>
              <BoxComponent :mb="8">
                <TitleComponent title="Traffic source">
                  <template #tooltip>
                    <BoxComponent :w="280">
                      <TypographyComponent variant="title-medium">Traffic source</TypographyComponent>
                      <BoxComponent :mb="4" :mt="4">
                        <TypographyComponent variant="title-medium">Instagram</TypographyComponent>
                      </BoxComponent>
                      <TypographyComponent variant="body-medium"> Traffic coming from native Instagram app. </TypographyComponent>
                      <BoxComponent :mb="4" :mt="16">
                        <TypographyComponent variant="title-medium">TikTok</TypographyComponent>
                      </BoxComponent>
                      <TypographyComponent variant="body-medium"> Traffic coming from native TikTok app. </TypographyComponent>
                      <BoxComponent :mb="4" :mt="16">
                        <TypographyComponent variant="title-medium">Other</TypographyComponent>
                      </BoxComponent>
                      <TypographyComponent variant="body-medium">
                        Traffic coming from any other source, e.g. websites (blogs, YouTube), messaging apps (WhatsApp, Telegram) and manual
                        link sharing.
                      </TypographyComponent>
                    </BoxComponent>
                  </template>
                </TitleComponent>
              </BoxComponent>
            </template>
            <template #footer>
              <button class="select-all-btn w-100" type="button" @click="handleAllSelect(item, index)">
                <TypographyComponent is="span" variant="body-large">
                  {{ item.sourceIndexes.length < trafficSources.length ? 'Select all' : 'Unselect all' }}
                </TypographyComponent>
              </button>
            </template>
          </SelectComponent>
        </BoxComponent>
        <BoxComponent v-else :mb="16">
          <BoxComponent :mb="8">
            <TitleComponent title="Traffic source" />
          </BoxComponent>
          <BoxComponent :h="56">
            <LoadingComponent :size="30" />
          </BoxComponent>
        </BoxComponent>
        <FlexComponent :gap="16" class="inputs-container" wrap="wrap">
          <div class="input-item">
            <el-form-item :prop="`trafficSourceCosts.${index}.cpc.amount`" :rules="cpcRules">
              <InputComponent v-model.number="item.cpc.amount">
                <template #header>
                  <BoxComponent :mb="8">
                    <TitleComponent title="Cost per click" />
                  </BoxComponent>
                </template>
                <template #suffix>
                  <TypographyComponent variant="title-large"> {{ item.cpc.currency }} / Click </TypographyComponent>
                </template>
              </InputComponent>
            </el-form-item>
          </div>

          <div v-if="invoiceAdvertiserDirectly" class="input-item">
            <el-form-item :prop="`trafficSourceCosts.${index}.invoiceRateCpc`" :rules="cpcRules">
              <InputComponent v-model="item.invoiceRateCpc as number" :name="`trafficSourceCosts.${index}.invoiceRateCpc`">
                <template #header>
                  <BoxComponent :mb="8">
                    <TitleComponent title="Invoice cost per click" />
                  </BoxComponent>
                </template>
                <template #suffix>
                  <TypographyComponent variant="title-large"> {{ props.currency }} / Click </TypographyComponent>
                </template>
              </InputComponent>
            </el-form-item>
          </div>

          <div class="input-item">
            <el-form-item :prop="`trafficSourceCosts.${index}.rateCpa`" step="any" :rules="cpaRules">
              <InputComponent v-model="item.rateCpa as number" type="number" step="any">
                <template #header>
                  <BoxComponent :mb="8">
                    <TitleComponent title="Cost per action" />
                  </BoxComponent>
                </template>

                <template #suffix>
                  <TypographyComponent variant="title-large">% / Sale</TypographyComponent>
                </template>
              </InputComponent>
            </el-form-item>
          </div>

          <div v-if="invoiceAdvertiserDirectly" class="input-item">
            <BoxComponent>
              <el-form-item :prop="`trafficSourceCosts.${index}.invoiceRateCpa`" step="any" :rules="cpaRules">
                <InputComponent v-model="item.invoiceRateCpa as number" type="number" step="any" data-test="invoiceCpa">
                  <template #header>
                    <BoxComponent :mb="8">
                      <TitleComponent title="Invoice cost per action" />
                    </BoxComponent>
                  </template>

                  <template #suffix>
                    <TypographyComponent variant="title-large">% / Sale</TypographyComponent>
                  </template>
                </InputComponent>
              </el-form-item>
            </BoxComponent>
          </div>
        </FlexComponent>
      </div>
      <ButtonComponent v-if="!isChangingSourcesDisabled" variant="transparent" is-icon @click="remove(item)">
        <el-icon :size="24" color="var(--secondary-64)">
          <DeleteIcon />
        </el-icon>
      </ButtonComponent>
    </FlexComponent>
  </BoxComponent>
  <ButtonComponent v-if="!isChangingSourcesDisabled" variant="outlined" @click="addItem">
    <template #iconLeft>
      <el-icon :size="24">
        <AddPlusIcon />
      </el-icon>
    </template>
    Add another source
  </ButtonComponent>
</template>

<style scoped lang="scss">
.container {
  border-radius: 16px;
  border: 1px solid var(--secondary-12);
}

.input-item {
  width: calc(50% - 8px);
}

.select-all-btn {
  justify-content: start;
  padding: 16px;
  border: none;
  text-align: left;
  background: none;
  cursor: pointer;
  border-radius: 0 0 16px 16px;

  &:hover {
    background: var(--secondary-8);
  }
}
</style>
